import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import ExploreMore from "@/components/ExploreMore";
import usePageSEOOverride from "@/hooks/useSEOOverrides";

const IMG = {
  hero: "https://static.prod-images.emergentagent.com/jobs/037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/images/3fe4c6ad3ce4a507c2f2092e1e8244712a79423da30f9f69cb463d2953b70049.png",
  challenge: "https://static.prod-images.emergentagent.com/jobs/037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/images/43e177093be4985fe47f507684daccccd03816cf207be0c3e0d99d4291e63f1c.png",
  solution: "https://static.prod-images.emergentagent.com/jobs/037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/images/765c10d894aed56889af6111aad1caa7f706b8d92a0ed4c42dbe461e6b43169b.png",
  result: "https://static.prod-images.emergentagent.com/jobs/037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/images/516564157977c4ea52654801be686b4652cb42ee22b8811eb55302be16172a3b.png",
};

export default function AboutPage() {
  usePageSEOOverride("/about", {
    title: "About Ezeefit | Office Furniture Manufacturer Mumbai Since 2014",
    description: "About Ezeefit — modular office furniture manufacturer in Mumbai with a Bhiwandi factory. Designing, building & installing corporate workspaces since 2014.",
    canonical: "https://ezeefitofficesystems.in/about",
  });

  return (
    <div className="pt-20" data-testid="about-page">
      <Helmet>
        <title>About Ezeefit | Office Furniture Manufacturer Mumbai Since 2014</title>
        <meta name="description" content="About Ezeefit — modular office furniture manufacturer in Mumbai with a Bhiwandi factory. Designing, building & installing corporate workspaces since 2014." />
        <link rel="canonical" href="https://ezeefitofficesystems.in/about" />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({"@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{"@type": "ListItem", "position": 1, "name": "Home", "item": "https://ezeefitofficesystems.in"}, {"@type": "ListItem", "position": 2, "name": "About Us", "item": "https://ezeefitofficesystems.in/about"}]}) }} />

      {/* Hero — compact, text-focused */}
      <section className="py-16 md:py-20 bg-[#0F172A]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Link to="/" className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white mb-6 transition-colors text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Link>
            {/* Animated brand mark — autoplays, muted, loops. Clipped to a circle
                so the white square background of the source MP4 stays hidden. */}
            <div
              className="mx-auto mb-6 relative overflow-hidden"
              style={{
                width: "160px",
                height: "160px",
                borderRadius: "50%",
                clipPath: "circle(50% at 50% 50%)",
                boxShadow: "0 20px 45px rgba(0,0,0,0.4)",
              }}
              data-testid="about-animated-logo-wrapper"
            >
              <video
                src="/media/animated-logo.mp4"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
                aria-hidden="true"
                data-testid="about-animated-logo"
              />
            </div>
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-3 block">About Us</span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">Our Story</h1>
            <p className="text-lg text-[#94A3B8] max-w-xl mx-auto">
              From interior design firm to India's trusted modular office furniture partner.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Story Content */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">

          {/* Opening */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="grid md:grid-cols-[1fr_400px] gap-10 items-start mb-16"
          >
            <div>
              <p className="text-lg text-[#334155] leading-relaxed mb-5">
                Ezeefit began its journey as an interior design firm, delivering projects from design conceptualization to turnkey execution. Over 12 years of manufacturing excellence, we evolved into a workspace transformation specialist — a precision engineering powerhouse now serving corporates pan-India.
              </p>
              <p className="text-[#64748B] leading-relaxed">
                During this phase, we identified a critical challenge faced by startups, corporates, and expanding businesses — setting up office spaces was often chaotic, time-consuming, and operationally draining. The inevitable on-site mess from traditional carpentry was costing businesses weeks of lost productivity.
              </p>
            </div>
            <img src={IMG.hero}
              alt="Line art illustration of interior design studio evolving into modern corporate office"
              className="w-full opacity-70 hidden md:block" loading="lazy" decoding="async" />
          </motion.div>

          {/* The Challenge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="grid md:grid-cols-[280px_1fr] gap-10 items-start mb-16"
          >
            <img src={IMG.challenge}
              alt="Line art illustration of chaotic traditional office construction with scattered materials and delays"
              className="w-full opacity-60 hidden md:block" loading="lazy" decoding="async" />
            <div>
              <p className="text-[#64748B] leading-relaxed mb-5">
                Traditional on-site carpentry and fragmented vendor coordination led to:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3 text-[#334155]">
                  <span className="w-1.5 h-1.5 bg-[#F97316] rounded-full mt-2.5 shrink-0" />
                  Delays in project completion
                </li>
                <li className="flex items-start gap-3 text-[#334155]">
                  <span className="w-1.5 h-1.5 bg-[#F97316] rounded-full mt-2.5 shrink-0" />
                  Lack of standardization and quality control
                </li>
                <li className="flex items-start gap-3 text-[#334155]">
                  <span className="w-1.5 h-1.5 bg-[#F97316] rounded-full mt-2.5 shrink-0" />
                  Loss of valuable time, energy, and management focus
                </li>
              </ul>
              <p className="text-[#64748B] leading-relaxed">
                For growing businesses, this directly impacted productivity and slowed down expansion.
              </p>
            </div>
          </motion.div>

          {/* The Turning Point */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="border-l-4 border-[#F97316] pl-6 md:pl-8 py-2 mb-16"
          >
            <p className="text-xl md:text-2xl font-bold text-[#0F172A]">
              Recognizing this gap, we built a solution.
            </p>
          </motion.div>

          {/* The Solution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="grid md:grid-cols-[1fr_280px] gap-10 items-start mb-16"
          >
            <div>
              <p className="text-[#64748B] leading-relaxed mb-5">
                At Ezeefit, we follow a modular, system-driven approach to office furniture and workspace execution — designed to eliminate on-site inefficiencies and streamline the entire process. Pre-engineered components allow professional-grade office assembly in hours rather than weeks of traditional carpentry.
              </p>
              <p className="text-[#64748B] leading-relaxed mb-5">
                Our manufacturing heritage combined with end-to-end project execution provides SMEs and corporate clients the tools to build a workspace without traditional lead-time hurdles.
              </p>
              <p className="text-[#64748B] leading-relaxed">
                Our factory-direct supply model delivers precision-engineered modular furniture — featuring acoustic membranes, integrated power modules and advanced cable management — directly from our Mumbai manufacturing facility to corporate offices.
              </p>
            </div>
            <img src={IMG.solution}
              alt="Line art illustration of automated modular furniture manufacturing assembly line"
              className="w-full opacity-60 hidden md:block" loading="lazy" decoding="async" />
          </motion.div>

          {/* The Promise */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="grid md:grid-cols-[400px_1fr] gap-10 items-start"
          >
            <img src={IMG.result}
              alt="Line art illustration of completed modern corporate office with modular workstations"
              className="w-full opacity-60 hidden md:block" loading="lazy" decoding="async" />
            <div>
              <p className="text-[#64748B] leading-relaxed mb-5">
                We take complete ownership of the workspace setup, ensuring:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3 text-[#334155]">
                  <span className="w-1.5 h-1.5 bg-[#F97316] rounded-full mt-2.5 shrink-0" />
                  Timely execution
                </li>
                <li className="flex items-start gap-3 text-[#334155]">
                  <span className="w-1.5 h-1.5 bg-[#F97316] rounded-full mt-2.5 shrink-0" />
                  Organized processes
                </li>
                <li className="flex items-start gap-3 text-[#334155]">
                  <span className="w-1.5 h-1.5 bg-[#F97316] rounded-full mt-2.5 shrink-0" />
                  Consistent quality standards
                </li>
              </ul>
              <p className="text-lg text-[#334155] leading-relaxed font-medium">
                So that our clients experience a stress-free, reliable, and scalable office setup solution.
              </p>
            </div>
          </motion.div>

        </div>
      </section>

      {/* Verifiable Credentials Strip */}
      <section className="py-14 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-wrap justify-center gap-10 md:gap-16 text-center">
            {[
              { value: "2014", label: "Established" },
              { value: "ISO 9001:2015", label: "Certified Products" },
              { value: "Mumbai", label: "Own Manufacturing Facility" },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <p className="text-3xl font-bold text-[#F97316]">{stat.value}</p>
                <p className="text-sm text-[#64748B] mt-1">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0F172A]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Let's Build Your Workspace
          </h2>
          <p className="text-[#94A3B8] mb-8">
            Partner with us for a stress-free, scalable office setup.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-8 py-5 font-semibold uppercase tracking-wider text-sm">
              <Link to="/contact">
                Request a Consultation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-none px-8 py-5 font-semibold uppercase tracking-wider text-sm">
              <Link to="/case-studies">
                View Our Projects
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <ExploreMore currentPage="manufacturing" />
    </div>
  );
}
